import React, { useState, useMemo } from "react";
import { Table, DatePicker, Checkbox, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetSalesReportSalesLedger } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const SalesLedgerReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);
  const [ledgerTypes, setLedgerTypes] = useState<string[]>([
    "sales",
    "credit_sales",
    "credit_payments",
    "services",
    "returns",
    "expenses",
  ]);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sales-ledger", startDateStr, endDateStr, ledgerTypes.join(",")],
    queryFn: () =>
      GetSalesReportSalesLedger({
        start_date: startDateStr,
        end_date: endDateStr,
        types: ledgerTypes.join(","),
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const inflow = reportData.reduce(
      (sum: number, item: any) => sum + (item.inflow || 0),
      0,
    );
    const outflow = reportData.reduce(
      (sum: number, item: any) => sum + (item.outflow || 0),
      0,
    );
    const balance = inflow - outflow;
    return [
      {
        title: "Total Cash-In (Inflow)",
        value: formatLKR(inflow),
        colorClass: "text-emerald-600",
      },
      {
        title: "Total Cash-Out (Outflow)",
        value: formatLKR(outflow),
        colorClass: "text-rose-600",
      },
      {
        title: "Net Balance Flow",
        value: formatLKR(balance),
        colorClass: balance >= 0 ? "text-emerald-700" : "text-rose-700",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Transaction Date",
      dataIndex: "transaction_date",
      key: "transaction_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Reference No",
      dataIndex: "reference_no",
      key: "reference_no",
      render: (v: string) => (
        <span className="text-xs font-bold text-gray-800">{v || "—"}</span>
      ),
    },
    {
      title: "Category Type",
      dataIndex: "type",
      key: "type",
      render: (v: string) => {
        let color = "default";
        let label = v;
        if (v === "sales") {
          color = "success";
          label = "Complete Sale";
        }
        if (v === "credit_sales") {
          color = "warning";
          label = "Credit Sale";
        }
        if (v === "credit_payments") {
          color = "processing";
          label = "Credit Payment";
        }
        if (v === "services") {
          color = "cyan";
          label = "Other Income";
        }
        if (v === "returns") {
          color = "error";
          label = "Customer Return";
        }
        if (v === "expenses") {
          color = "red";
          label = "Expense";
        }
        return (
          <Tag
            color={color}
            className="rounded-full font-semibold text-[10px] uppercase"
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (v: string) => (
        <span className="text-xs text-gray-655 font-medium">{v}</span>
      ),
    },
    {
      title: "Cash In (Inflow)",
      dataIndex: "inflow",
      key: "inflow",
      align: "right" as const,
      render: (v: number) =>
        v > 0 ? (
          <span className="text-xs font-bold text-emerald-600">
            +{v.toFixed(2)}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Cash Out (Outflow)",
      dataIndex: "outflow",
      key: "outflow",
      align: "right" as const,
      render: (v: number) =>
        v > 0 ? (
          <span className="text-xs font-bold text-rose-500">
            -{v.toFixed(2)}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Running Balance",
      dataIndex: "running_balance",
      key: "running_balance",
      align: "right" as const,
      render: (v: number) => (
        <span
          className={`text-xs font-extrabold ${v >= 0 ? "text-emerald-700" : "text-rose-700"}`}
        >
          {v.toFixed(2)}
        </span>
      ),
    },
  ];

  const filterControls = (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1.5 font-sans">
          <span className="text-[10px] font-bold text-gray-400 uppercase">
            Reporting Date Period
          </span>
          <RangePicker
            value={dateRange}
            onChange={(dates) =>
              dates &&
              dates[0] &&
              dates[1] &&
              setDateRange([dates[0], dates[1]])
            }
            allowClear={false}
            className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
          />
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4 flex flex-col gap-2 w-full font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Filter Transaction Categories
        </span>
        <div className="flex flex-wrap gap-x-6 gap-y-2.5">
          {[
            { label: "Complete Sales", value: "sales" },
            { label: "Credit Sales", value: "credit_sales" },
            { label: "Credit Payments", value: "credit_payments" },
            { label: "Services / Other Incomes", value: "services" },
            { label: "Returns / Refunds", value: "returns" },
            { label: "Expenses", value: "expenses" },
          ].map((opt) => (
            <Checkbox
              key={opt.value}
              checked={ledgerTypes.includes(opt.value)}
              onChange={(e) => {
                const nextVal = e.target.checked
                  ? [...ledgerTypes, opt.value]
                  : ledgerTypes.filter((x) => x !== opt.value);
                setLedgerTypes(nextVal);
              }}
              className="text-xs font-medium text-gray-655"
            >
              {opt.label}
            </Checkbox>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <ReportPage
      title="Sales & Expense Ledger"
      description="Unified cash book of sales, credit payments, incomes, and expenses with a running balance."
      icon={<TrendingUp className="w-6 h-6 text-emerald-600" />}
      backPath="/system/sales-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) => record.id || Math.random().toString()}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
